/**
 * ============================================
 * SessionTimeoutModal Component
 * ============================================
 *
 * Displays a warning modal when session is about to expire
 * Shows countdown timer and allows session extension
 */

import React from 'react';
import { Modal } from '../admin/Modal';
import { AUTH_MESSAGES } from '../../constants/authMessages';
import './SessionTimeoutModal.css';

/**
 * Format milliseconds to MM:SS
 * @param {number} ms - Milliseconds
 * @returns {string} - Formatted time string
 */
const formatTime = (ms) => {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Session timeout warning modal
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility
 * @param {number} props.remainingTime - Remaining time in milliseconds
 * @param {Function} props.onExtend - Handler for extending session
 * @param {Function} props.onLogout - Handler for immediate logout
 */
export function SessionTimeoutModal({ isOpen, remainingTime, onExtend, onLogout }) {
  const formattedTime = formatTime(remainingTime);
  const isUrgent = remainingTime <= 60000; // 1분 이하

  return (
    <Modal
      isOpen={isOpen}
      onClose={onExtend}
      title={AUTH_MESSAGES.SESSION_TIMEOUT_TITLE}
      size="small"
    >
      <div className="session-timeout-content">
        <div className="session-timeout-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            width="48"
            height="48"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
          </svg>
        </div>

        <p className="session-timeout-message">{AUTH_MESSAGES.SESSION_TIMEOUT_MESSAGE}</p>

        <div className={`session-timeout-timer ${isUrgent ? 'urgent' : ''}`}>
          <span className="timer-label">{AUTH_MESSAGES.SESSION_TIMEOUT_REMAINING}</span>
          <span className="timer-value">{formattedTime}</span>
        </div>

        <div className="session-timeout-actions">
          <button type="button" className="ds-button" onClick={onLogout}>
            {AUTH_MESSAGES.SESSION_TIMEOUT_LOGOUT_BUTTON}
          </button>
          <button
            type="button"
            className="ds-button ds-button-primary"
            onClick={onExtend}
            autoFocus
          >
            {AUTH_MESSAGES.SESSION_TIMEOUT_EXTEND_BUTTON}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default SessionTimeoutModal;
